/**
 * `/admin/waitlist` — pending applications. Accept turns a row into an
 * invite + email.
 */

import { useCallback, useEffect, useState } from 'react';
import type { AdminWaitlistRow } from '@eloquor/api-contract';
import { acceptWaitlist, listWaitlist } from '../../api/admin';

export function AdminWaitlistPage() {
  const [rows, setRows] = useState<AdminWaitlistRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const refresh = useCallback(async () => {
    const { rows: list } = await listWaitlist();
    setRows(list);
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleAccept(id: string) {
    if (!confirm('Accept this applicant and send them an invite email?')) return;
    await acceptWaitlist(id);
    await refresh();
  }

  return (
    <>
      <div className="auth-eyebrow"><span className="num">ADMIN</span> · Waitlist</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Waitlist ({rows.filter((r) => !r.acceptedAt).length} pending)
      </h1>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '8px 12px' }}>Applied</th>
            <th style={{ padding: '8px 12px' }}>Name</th>
            <th style={{ padding: '8px 12px' }}>Email</th>
            <th style={{ padding: '8px 12px' }}>School</th>
            <th style={{ padding: '8px 12px' }}>Event / Circuit</th>
            <th style={{ padding: '8px 12px' }}>Why you</th>
            <th style={{ padding: '8px 12px' }}>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                {new Date(r.appliedAt).toLocaleDateString()}
              </td>
              <td style={{ padding: '10px 12px' }}>{r.name}</td>
              <td style={{ padding: '10px 12px', fontSize: 12 }}>{r.email}</td>
              <td style={{ padding: '10px 12px' }}>{r.school}</td>
              <td style={{ padding: '10px 12px', color: 'var(--fg-2)' }}>
                {r.event} · {r.circuit}
              </td>
              <td style={{ padding: '10px 12px', color: 'var(--fg-2)', maxWidth: 240 }}>
                {expanded.has(r.id) ? (
                  <>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{r.whyYou}</span>{' '}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(r.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--fg-3)',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 11,
                      }}
                    >
                      ▾ collapse
                    </button>
                  </>
                ) : (
                  <>
                    {r.whyYou.length > 60 ? `${r.whyYou.slice(0, 60)}…` : r.whyYou}
                    {r.whyYou.length > 60 && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => toggleExpanded(r.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--fg-3)',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 11,
                          }}
                        >
                          ▸
                        </button>
                      </>
                    )}
                  </>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                {r.acceptedAt ? (
                  <span style={{ color: 'var(--rt-ok-ink)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    accepted · {r.acceptedInviteCode}
                  </span>
                ) : (
                  <span style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>pending</span>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                {!r.acceptedAt && (
                  <button className="btn-primary" style={{ width: 'auto', padding: '6px 12px', fontSize: 12, margin: 0 }} onClick={() => handleAccept(r.id)}>
                    Accept
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
